// =============================================================================
// SHARED I18N — SINGLE FAILURE HANDLER
// =============================================================================
// One place, one rule for "this string cannot be resolved":
//   - dev  → throw (fail loud; the developer sees it immediately)
//   - prod → render a visible marker and console.error (never crash the React
//            subtree — one bad API value must not blank a whole page)
//
// Two callers share this rule so the behaviour can never drift:
//   1. The exhaustive enum maps (Step-0 PR-0C-1) — a present-but-unmapped runtime
//      value (e.g. an attendance status off the DB CHECK contract).
//   2. The i18nStore lookup miss (Step-0 PR-0C-2) — a key absent from the current
//      locale bundle once the fallback chain is removed.
//
// ABSENCE IS NOT A MISS. An optional/nullable field being null/undefined is a
// legitimate runtime state, handled by the caller as its own branch (render
// nothing) BEFORE reaching here. Only a present, off-contract value is a miss.
// =============================================================================

import { config } from '../config';

/** True in a Vite dev build; false in prod and safe under vitest/SSR/node. */
function isDevEnv(): boolean {
  try {
    // import.meta.env is injected by Vite; guard so this file is safe anywhere.
    return Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    return false;
  }
}

/** Marker rendered in prod so a miss is visible in the UI without crashing it. */
export function i18nMarker(context: string): string {
  return `⟦${context}⟧`; // ⟦context⟧
}

// Report each distinct context once per session — a miss loops over a list, so
// without this a single bad value would flood the ingest route.
const _reported = new Set<string>();

/**
 * Ship a prod miss to the backend (POST /api/i18n/miss → CloudWatch) so a human
 * sees it, not just the user's browser console. Fire-and-forget and fully
 * defensive: telemetry must never throw into the render path, and it degrades to
 * a no-op anywhere `fetch`/`navigator` are absent (vitest/SSR).
 */
function reportToBackend(context: string): void {
  if (_reported.has(context)) return;
  _reported.add(context);
  try {
    const url = `${config.apiBase}/api/i18n/miss`;
    const payload = {
      context,
      path: typeof location !== 'undefined' ? location.pathname : undefined,
    };
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else if (typeof fetch === 'function') {
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => { /* swallow — best-effort telemetry */ });
    }
  } catch {
    /* never let telemetry break rendering */
  }
}

/**
 * The one failure path. Throws in dev, returns a visible marker in prod, and in
 * prod also reports the miss to the backend so it reaches CloudWatch.
 * @param context short human-readable locus, e.g. `subject category "quantum"`.
 */
export function reportI18nMiss(context: string): string {
  const message = `[i18n] unresolved: ${context}`;
  if (isDevEnv()) throw new Error(message);
  if (typeof console !== 'undefined') console.error(message);
  reportToBackend(context);
  return i18nMarker(context);
}

/**
 * Resolve a value to its translated string through an exhaustive map.
 * The map is typed Record<K, string>, so the compiler already forces every
 * union member to be present; this adds the runtime guard for values that
 * arrive off-contract from an untyped API boundary (TS cannot see those).
 *
 * Callers MUST branch on absence (null/undefined) before calling — this takes a
 * concrete K only, keeping "absent" and "unmapped" as distinct outcomes.
 *
 * @param t      the translation function from useTranslation()
 * @param map    exhaustive key map over the value's domain
 * @param value  a present, in-domain value (caller has excluded null/undefined)
 * @param label  domain name for the miss message, e.g. `attendance status`
 */
export function tEnum<K extends string>(
  t: (key: string) => string,
  map: Record<K, string>,
  value: K,
  label: string,
): string {
  const key = (map as Record<string, string>)[value];
  if (key === undefined) return reportI18nMiss(`${label} "${String(value)}"`);
  return t(key);
}
